export type Lang = "en" | "fr" | "de";

export const LANGS: Lang[] = ["en", "fr", "de"];

export const LANG_LABELS: Record<Lang, string> = {
  en: "EN",
  fr: "FR",
  de: "DE",
};

export const TALLY_URL = "https://tally.so/r/gDEk4O";
export const CAL_URL = "https://cal.com/galkin/30min";

export interface Translation {
  htmlLang: string;
  nav: {
    home: string;
    about: string;
    solutions: string;
    clients: string;
    contacts: string;
    cta: string;
  };
  hero: {
    h1: string;
    sub: string;
    sliderLabel: string;
    cta: string;
  };
  process: {
    title: string;
    steps: { title: string; desc: string }[];
  };
  proof: {
    title: string;
    cards: { title: string; time: string; date: string }[];
  };
  useCases: {
    title: string;
    cards: { title: string; desc: string }[];
  };
  founders: {
    title: string;
    people: { name: string; role: string }[];
  };
  wallOfFame: {
    title: string;
    subtitle: string;
    chips: { topic: string; time: string }[];
  };
  footer: {
    bookCall: string;
    waitlist: string;
    copyright: string;
  };
}

const en: Translation = {
  htmlLang: "en",
  nav: {
    home: "Home",
    about: "About",
    solutions: "Solutions",
    clients: "Clients",
    contacts: "Contacts",
    cta: "Join the waitlist",
  },
  hero: {
    h1: "SpeakSprout: Master New Topics in Minutes",
    sub: "Generate a clear learning sprint, speak your understanding out loud and get feedback that turns curiosity into confidence.",
    sliderLabel: "Sprint length",
    cta: "Start your first sprint",
  },
  process: {
    title: "A focused ritual for the curious mind",
    steps: [
      { title: "Choose", desc: "A topic worth knowing" },
      { title: "Study", desc: "A guided focus sprint" },
      { title: "1-Min Pitch", desc: "Say it in your own words" },
      { title: "AI Review", desc: "Receive your next insight" },
    ],
  },
  proof: {
    title: "Learning artifacts that speak for themselves",
    cards: [
      { title: "Quantum Theory Mastered", time: "25 min", date: "2 days ago" },
      { title: "Blockchain Fundamentals", time: "20 min", date: "5 days ago" },
      { title: "Stoic Philosophy Basics", time: "15 min", date: "1 week ago" },
      { title: "Neural Networks 101", time: "30 min", date: "2 weeks ago" },
    ],
  },
  useCases: {
    title: "Practice for every learner",
    cards: [
      {
        title: "Self-Growth",
        desc: "Build a daily practice. Choose a topic, sprint through it, and speak your understanding into existence.",
      },
      {
        title: "Discussion Clubs",
        desc: "Walk in with sharper ideas. Use sprints to prepare your talking points and get AI feedback before the debate.",
      },
      {
        title: "Educational Checkpoint",
        desc: "Assess understanding, not memorization. Let learners pitch what they know and get instant feedback.",
      },
    ],
  },
  founders: {
    title: "The Founders",
    people: [
      { name: "Cyril Galkin", role: "CTO" },
      { name: "Polina Markova", role: "CMO" },
    ],
  },
  wallOfFame: {
    title: "Wall of Fame",
    subtitle: "Topics mastered by our community this week.",
    chips: [
      { topic: "Quantum Entanglement", time: "25 min" },
      { topic: "Behavioral Economics", time: "20 min" },
      { topic: "Existentialism", time: "15 min" },
      { topic: "Photosynthesis", time: "10 min" },
      { topic: "Game Theory", time: "30 min" },
      { topic: "Mars Colonization", time: "25 min" },
    ],
  },
  footer: {
    bookCall: "Book a call",
    waitlist: "Join the waitlist",
    copyright: "© 2026 SpeakSprout. All rights reserved.",
  },
};

const fr: Translation = {
  htmlLang: "fr",
  nav: {
    home: "Accueil",
    about: "À propos",
    solutions: "Solutions",
    clients: "Clients",
    contacts: "Contacts",
    cta: "Rejoindre la liste d'attente",
  },
  hero: {
    h1: "SpeakSprout : Maîtrisez de nouveaux sujets en quelques minutes",
    sub: "Générez un sprint d'apprentissage clair, exprimez votre compréhension à voix haute et obtenez un retour qui transforme la curiosité en confiance.",
    sliderLabel: "Durée du sprint",
    cta: "Lancez votre premier sprint",
  },
  process: {
    title: "Un rituel ciblé pour l'esprit curieux",
    steps: [
      { title: "Choisir", desc: "Un sujet qui mérite d'être connu" },
      { title: "Étudier", desc: "Un sprint guidé et concentré" },
      { title: "Pitch d'1 min", desc: "Dites-le avec vos propres mots" },
      { title: "Revue IA", desc: "Recevez votre prochaine idée" },
    ],
  },
  proof: {
    title: "Des artefacts d'apprentissage qui parlent d'eux-mêmes",
    cards: [
      {
        title: "Théorie Quantique Maîtrisée",
        time: "25 min",
        date: "il y a 2 jours",
      },
      {
        title: "Fondamentaux de la Blockchain",
        time: "20 min",
        date: "il y a 5 jours",
      },
      {
        title: "Bases du Stoïcisme",
        time: "15 min",
        date: "il y a 1 semaine",
      },
      { title: "Réseaux de Neurones 101", time: "30 min", date: "il y a 2 semaines" },
    ],
  },
  useCases: {
    title: "De la pratique pour chaque apprenant",
    cards: [
      {
        title: "Développement Personnel",
        desc: "Construisez une pratique quotidienne. Choisissez un sujet, sprintez-le, et donnez vie à votre compréhension.",
      },
      {
        title: "Clubs de Discussion",
        desc: "Arrivez avec des idées plus fines. Utilisez les sprints pour préparer vos arguments et obtenez un retour IA avant le débat.",
      },
      {
        title: "Point de Contrôle Pédagogique",
        desc: "Évaluez la compréhension, pas la mémorisation. Laissez les apprenants pitcher ce qu'ils savent et obtenez un retour instantané.",
      },
    ],
  },
  founders: {
    title: "Les Fondateurs",
    people: [
      { name: "Cyril Galkin", role: "CTO" },
      { name: "Polina Markova", role: "CMO" },
    ],
  },
  wallOfFame: {
    title: "Mur de la Renommée",
    subtitle: "Sujets maîtrisés par notre communauté cette semaine.",
    chips: [
      { topic: "Intrication Quantique", time: "25 min" },
      { topic: "Économie Comportementale", time: "20 min" },
      { topic: "Existentialisme", time: "15 min" },
      { topic: "Photosynthèse", time: "10 min" },
      { topic: "Théorie des Jeux", time: "30 min" },
      { topic: "Colonisation de Mars", time: "25 min" },
    ],
  },
  footer: {
    bookCall: "Réserver un appel",
    waitlist: "Rejoindre la liste d'attente",
    copyright: "© 2026 SpeakSprout. Tous droits réservés.",
  },
};

const de: Translation = {
  htmlLang: "de",
  nav: {
    home: "Startseite",
    about: "Über uns",
    solutions: "Lösungen",
    clients: "Kunden",
    contacts: "Kontakt",
    cta: "Zur Warteliste",
  },
  hero: {
    h1: "SpeakSprout: Neue Themen in Minuten meistern",
    sub: "Erstelle einen klaren Lern-Sprint, sprich dein Verständnis laut aus und erhalte Feedback, das Neugier in Zuversicht verwandelt.",
    sliderLabel: "Sprint-Länge",
    cta: "Starte deinen ersten Sprint",
  },
  process: {
    title: "Ein fokussiertes Ritual für den neugierigen Geist",
    steps: [
      { title: "Wählen", desc: "Ein Thema, das man kennen sollte" },
      { title: "Lernen", desc: "Ein geführter Fokus-Sprint" },
      { title: "1-Min-Pitch", desc: "Sag es mit deinen eigenen Worten" },
      { title: "KI-Review", desc: "Erhalte deine nächste Erkenntnis" },
    ],
  },
  proof: {
    title: "Lern-Artefakte, die für sich selbst sprechen",
    cards: [
      { title: "Quantentheorie gemeistert", time: "25 min", date: "vor 2 Tagen" },
      { title: "Blockchain-Grundlagen", time: "20 min", date: "vor 5 Tagen" },
      { title: "Stoische Philosophie", time: "15 min", date: "vor 1 Woche" },
      { title: "Neuronale Netze 101", time: "30 min", date: "vor 2 Wochen" },
    ],
  },
  useCases: {
    title: "Praxis für jeden Lernenden",
    cards: [
      {
        title: "Persönliches Wachstum",
        desc: "Baue eine tägliche Praxis auf. Wähle ein Thema, durchlaufe den Sprint und sprich dein Verständnis ins Leben.",
      },
      {
        title: "Diskussionsclubs",
        desc: "Komm mit schärferen Ideen. Nutze Sprints zur Vorbereitung deiner Argumente und erhalte KI-Feedback vor der Debatte.",
      },
      {
        title: "Bildungs-Checkpoint",
        desc: "Bewerte Verständnis, nicht Auswendiglernen. Lass Lernende pitchen, was sie wissen, und erhalte sofortiges Feedback.",
      },
    ],
  },
  founders: {
    title: "Die Gründer",
    people: [
      { name: "Cyril Galkin", role: "CTO" },
      { name: "Polina Markova", role: "CMO" },
    ],
  },
  wallOfFame: {
    title: "Wall of Fame",
    subtitle: "Themen, die unsere Community diese Woche gemeistert hat.",
    chips: [
      { topic: "Quantenverschränkung", time: "25 min" },
      { topic: "Verhaltensökonomie", time: "20 min" },
      { topic: "Existenzialismus", time: "15 min" },
      { topic: "Photosynthese", time: "10 min" },
      { topic: "Spieltheorie", time: "30 min" },
      { topic: "Marskolonisierung", time: "25 min" },
    ],
  },
  footer: {
    bookCall: "Anruf buchen",
    waitlist: "Zur Warteliste",
    copyright: "© 2026 SpeakSprout. Alle Rechte vorbehalten.",
  },
};

export const translations: Record<Lang, Translation> = { en, fr, de };
